import React, { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Nexus en petit robot vivant : ses yeux suivent la souris (et sa tête s'incline un peu vers elle), il cligne des yeux
// et flotte doucement ; il change selon ce qu'il fait (réfléchit, écrit, content, déçu, a un message : une lettre en main) et selon la page (couleur des
// yeux et de l'antenne, casque en réunion). Sans souris (téléphone), son regard se promène de temps en temps.
// `still` : version fixe (avatars des messages) ; rien ne bouge si l'utilisateur demande moins d'animations.

export const PAGE_LOOKS = [
  { match: /^\/(project|projects)\b/, color: '#38bdf8', label: 'projets' },
  { match: /^\/(expenses|billing|purchases|orders|invoices)/, color: '#34d399', label: 'dépenses' },
  { match: /^\/(holacracy|meetings|circle)/, color: '#fbbf24', label: 'réunions', headset: true },
  { match: /^\/(objectives|backlog|my-tasks|tasks)/, color: '#a78bfa', label: 'objectifs' },
  { match: /^\/(offers|guttenberg|tools)/, color: '#f472b6', label: 'offres' },
  { match: /^\/$/, color: '#818cf8', label: 'accueil' },
];
const DEFAULT_LOOK = { color: '#67e8f9', label: '' };

export const lookFor = (pathname = '') => PAGE_LOOKS.find((look) => look.match.test(pathname)) || DEFAULT_LOOK;

const useReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return reduced;
};

// Regard : vers la souris (bornes de quelques unités), sinon une petite promenade aléatoire.
const useGaze = (ref, enabled) => {
  useEffect(() => {
    const svg = ref.current;
    if (!enabled || !svg || typeof window === 'undefined') return undefined;
    const eyes = svg.querySelector('[data-eyes]');
    const head = svg.querySelector('[data-head]');
    let frame = null;
    let target = null;
    const apply = () => {
      frame = null;
      if (!target) return;
      const box = svg.getBoundingClientRect();
      const dx = target.x - (box.left + box.width / 2);
      const dy = target.y - (box.top + box.height / 2);
      const distance = Math.hypot(dx, dy) || 1;
      const reach = Math.min(1, distance / 260);
      const x = (dx / distance) * 4 * reach;
      const y = (dy / distance) * 3 * reach;
      eyes?.setAttribute('transform', `translate(${x.toFixed(2)} ${y.toFixed(2)})`);
      head?.setAttribute('transform', `rotate(${(x * 1.4).toFixed(2)} 32 36)`);
    };
    const onMove = (event) => {
      target = { x: event.clientX, y: event.clientY };
      if (!frame) frame = window.requestAnimationFrame(apply);
    };
    const finePointer = window.matchMedia?.('(pointer: fine)').matches;
    let wander = null;
    if (finePointer) {
      window.addEventListener('mousemove', onMove, { passive: true });
    } else {
      wander = window.setInterval(() => {
        const box = svg.getBoundingClientRect();
        const angle = Math.random() * Math.PI * 2;
        const near = Math.random() < 0.35; // de temps en temps, il regarde droit devant
        target = near ? { x: box.left + box.width / 2, y: box.top + box.height / 2 }
          : { x: box.left + box.width / 2 + Math.cos(angle) * 300, y: box.top + box.height / 2 + Math.sin(angle) * 200 };
        if (!frame) frame = window.requestAnimationFrame(apply);
      }, 2600);
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (wander) window.clearInterval(wander);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [ref, enabled]);
};

const NexusBot = ({ size = 32, mood = 'idle', still = false, track = false, pathname, className = '', title = 'Nexus' }) => {
  const id = useId().replace(/:/g, '');
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const live = !still && !reduced;
  useGaze(ref, live && track && (mood === 'idle' || mood === 'writing' || mood === 'message'));
  const look = lookFor(pathname ?? (typeof window !== 'undefined' ? window.location.pathname : ''));
  const color = look.color;
  const happy = mood === 'happy';
  const sad = mood === 'sad';
  const thinking = mood === 'thinking';
  const writing = mood === 'writing';
  const message = mood === 'message';
  const beacon = message ? '#f59e0b' : color; // antenne orange : un message attend

  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={look.label ? `${title} (${look.label})` : title}
      className={`nexus-bot ${live ? 'nexus-bot--live' : ''} nexus-bot--${mood} ${className}`}
      style={{ '--nexus-bot-color': color }}
    >
      <defs>
        <linearGradient id={`${id}-shell`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#e2e8f0" />
        </linearGradient>
        <linearGradient id={`${id}-screen`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1e1b4b" />
          <stop offset="100%" stopColor="#312e81" />
        </linearGradient>
        <radialGradient id={`${id}-glow`}>
          <stop offset="0%" stopColor={beacon} stopOpacity="0.9" />
          <stop offset="100%" stopColor={beacon} stopOpacity="0" />
        </radialGradient>
      </defs>
      <g className="nexus-bot__body">
        <g data-head="">
          {/* antenne */}
          <line x1="32" y1="13" x2="32" y2="6.5" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
          <circle className="nexus-bot__beacon-glow" cx="32" cy="5.5" r="6" fill={`url(#${id}-glow)`} />
          <circle className="nexus-bot__beacon" cx="32" cy="5.5" r="3" fill={beacon} />
          {/* oreilles */}
          <rect x="3.5" y="28" width="6" height="14" rx="3" fill={color} />
          <rect x="54.5" y="28" width="6" height="14" rx="3" fill={color} />
          {/* tête et écran */}
          <rect x="8" y="13" width="48" height="42" rx="15" fill={`url(#${id}-shell)`} stroke="#cbd5e1" strokeWidth="1.2" />
          <rect x="13.5" y="19.5" width="37" height="29" rx="10" fill={`url(#${id}-screen)`} />
          <rect x="16" y="21.5" width="14" height="4" rx="2" fill="#ffffff" fillOpacity="0.12" />
          {look.headset && (
            <g className="nexus-bot__headset">
              <path d="M6.5 33 C5 7 59 7 57.5 33" fill="none" stroke="#475569" strokeWidth="3.2" strokeLinecap="round" />
              <rect x="2.5" y="27" width="8" height="16" rx="4" fill="#475569" />
              <rect x="53.5" y="27" width="8" height="16" rx="4" fill="#475569" />
              <path d="M57.5 41 C57.5 49 51 53 43 53" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" />
              <circle cx="42" cy="53" r="2.4" fill={color} />
            </g>
          )}
          {/* yeux */}
          <g data-eyes="" className="nexus-bot__eyes">
            {happy ? (
              <g fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round">
                <path d="M20.5 33 Q24.5 28 28.5 33" />
                <path d="M35.5 33 Q39.5 28 43.5 33" />
              </g>
            ) : (
              <g className="nexus-bot__lids" fill={color}>
                <ellipse cx="24.5" cy={thinking ? 30.5 : 32} rx="3.6" ry={sad ? 3.2 : 4.6} />
                <ellipse cx="39.5" cy={thinking ? 30.5 : 32} rx="3.6" ry={sad ? 3.2 : 4.6} />
                <circle cx={thinking ? 26 : 25.6} cy={thinking ? 28.6 : 30.4} r="1.2" fill="#ffffff" fillOpacity="0.85" />
                <circle cx={thinking ? 41 : 40.6} cy={thinking ? 28.6 : 30.4} r="1.2" fill="#ffffff" fillOpacity="0.85" />
              </g>
            )}
            {sad && (
              <g stroke={color} strokeWidth="1.6" strokeLinecap="round">
                <path d="M20.5 26.5 L27 28" />
                <path d="M43.5 26.5 L37 28" />
              </g>
            )}
          </g>
          {/* bouche */}
          {thinking ? (
            <g className="nexus-bot__dots" fill={color}>
              <circle cx="26" cy="42" r="1.6" />
              <circle cx="32" cy="42" r="1.6" />
              <circle cx="38" cy="42" r="1.6" />
            </g>
          ) : writing ? (
            <rect className="nexus-bot__talk" x="27" y="40" width="10" height="4" rx="2" fill={color} />
          ) : sad ? (
            <path d="M27 43.5 Q32 40 37 43.5" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
          ) : (
            <path d={happy ? 'M25.5 40 Q32 46.5 38.5 40' : 'M27 41 Q32 44.5 37 41'} fill="none" stroke={color}
              strokeWidth="2" strokeLinecap="round" />
          )}
          {happy && (
            <g fill="#fb7185" fillOpacity="0.45">
              <ellipse cx="19" cy="39" rx="3" ry="1.8" />
              <ellipse cx="45" cy="39" rx="3" ry="1.8" />
            </g>
          )}
        </g>
        {message && (
          <g className="nexus-bot__letter">
            <rect x="37" y="41" width="22" height="15" rx="2.5" fill="#fffbeb" stroke="#f59e0b" strokeWidth="1.6" />
            <path d="M38 42.5 L48 50 L58 42.5" fill="none" stroke="#f59e0b" strokeWidth="1.6" strokeLinejoin="round" />
            <circle cx="57.5" cy="41.5" r="3.2" fill="#ef4444" stroke="#ffffff" strokeWidth="1.2" />
          </g>
        )}
      </g>
    </svg>
  );
};

NexusBot.propTypes = {
  size: PropTypes.number,
  mood: PropTypes.oneOf(['idle', 'thinking', 'writing', 'happy', 'sad', 'message']),
  still: PropTypes.bool,
  track: PropTypes.bool,
  pathname: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
};

export default NexusBot;
